import { Grid, Card } from '@mui/material';

import { CompanyDetailsResponse } from '@/models/payment';
import { SubTitle, Title } from '@/typography/Typography';
import { FormActionButtons } from '@/components/shared/FormActionButtons';
import { InputText } from '@/components/shared/InputText';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useErrorHandling } from '@/utils/errorHandling';
import {
    GridContainer,
    IntegrationCard,
    IntegrationCardPadding
} from '@/pages/company/componants/Base';

export const WithdrawalLimitsSettings = ({
    currentCompany
}: {
    currentCompany?: CompanyDetailsResponse;
}) => {
    const { t } = useTranslation();

    const [withdrawalCountPerDay, setWithdrawalLimitForDay] =
        useState<number>();
    const [withdrawalMaxAmountPerLast7Days, setWithdrawalLimitFor7Days] =
        useState<number>();
    const [
        withdrawalMaxAmountPerTransaction,
        setWithdrawalLimitForPerTransaction
    ] = useState<number>();

    // Track initial values for dirty state detection
    const [initialWithdrawalCountPerDay, setInitialWithdrawalCountPerDay] =
        useState<number>();
    const [
        initialWithdrawalMaxAmountPerLast7Days,
        setInitialWithdrawalMaxAmountPerLast7Days
    ] = useState<number>();
    const [
        initialWithdrawalMaxAmountPerTransaction,
        setInitialWithdrawalMaxAmountPerTransaction
    ] = useState<number>();

    // Compute if form is dirty
    const isDirty =
        withdrawalCountPerDay != initialWithdrawalCountPerDay ||
        withdrawalMaxAmountPerLast7Days !=
            initialWithdrawalMaxAmountPerLast7Days ||
        withdrawalMaxAmountPerTransaction !=
            initialWithdrawalMaxAmountPerTransaction;

    const initialValues = () => {
        const perDay = currentCompany?.withdrawalCountPerDay;
        const per7Days = currentCompany?.withdrawalMaxAmountPerLast7Days;
        const perTransaction =
            currentCompany?.withdrawalMaxAmountPerTransaction;

        setWithdrawalLimitForDay(perDay);
        setWithdrawalLimitFor7Days(per7Days);
        setWithdrawalLimitForPerTransaction(perTransaction);

        // Set initial values for dirty tracking
        setInitialWithdrawalCountPerDay(perDay);
        setInitialWithdrawalMaxAmountPerLast7Days(per7Days);
        setInitialWithdrawalMaxAmountPerTransaction(perTransaction);
    };

    useEffect(() => {
        initialValues();
    }, [
        currentCompany?.withdrawalCountPerDay,
        currentCompany?.withdrawalMaxAmountPerLast7Days,
        currentCompany?.withdrawalMaxAmountPerTransaction
    ]);

    const { updateCompanyDetails } = usePaymentsMutations();

    const { setAlert } = useNotificationsSnackbar();

    useEffect(() => {
        if (updateCompanyDetails.isSuccess) {
            setAlert(t('limits_updated'), 'success');
        }
    }, [updateCompanyDetails.isSuccess]);

    const { backendErrorHandler } = useErrorHandling();

    useEffect(() => {
        if (updateCompanyDetails.isError) {
            backendErrorHandler()(updateCompanyDetails.error);
        }
    }, [updateCompanyDetails.isError]);

    return (
        <Grid
            container
            mt={3}
            pt={5}
            justifyContent={'space-between'}
            borderTop={'1px solid #D0D5DD'}
        >
            <Grid item xs={3} display={'block'}>
                <Title>{t('withdrawal_limits')}</Title>
                <SubTitle marginBottom={2}>
                    {t('withdrawal_limits_note')}
                </SubTitle>
            </Grid>
            <Grid item xs={8.8}>
                <IntegrationCard>
                    <IntegrationCardPadding>
                        <InputText
                            type="number"
                            value={withdrawalCountPerDay}
                            onChange={(e) => {
                                setWithdrawalLimitForDay(e.target.value as any);
                            }}
                            label={t('withdrawal_limit_for_day')}
                        />
                        <SubTitle>
                            {t('withdrawal_limit_for_day_note')}
                        </SubTitle>

                        <InputText
                            type="number"
                            value={withdrawalMaxAmountPerLast7Days}
                            onChange={(e) => {
                                setWithdrawalLimitFor7Days(
                                    e.target.value as any
                                );
                            }}
                            label={t('withdrawal_limit_for_7_days', {
                                currency: currentCompany?.currency
                            })}
                        />
                        <SubTitle>
                            {t('withdrawal_limit_for_7_days_note')}
                        </SubTitle>
                        <InputText
                            type="number"
                            value={withdrawalMaxAmountPerTransaction}
                            onChange={(e) => {
                                setWithdrawalLimitForPerTransaction(
                                    e.target.value as any
                                );
                            }}
                            label={t('withdrawal_limit_for_per_transaction', {
                                currency: currentCompany?.currency
                            })}
                        />
                        <SubTitle>
                            {t('withdrawal_limit_for_per_transaction_note')}
                        </SubTitle>
                    </IntegrationCardPadding>
                    <FormActionButtons
                        show={isDirty}
                        onSave={() => {
                            updateCompanyDetails.mutate({
                                withdrawalCountPerDay,
                                withdrawalMaxAmountPerLast7Days,
                                withdrawalMaxAmountPerTransaction
                            });
                        }}
                        onCancel={initialValues}
                        isLoading={updateCompanyDetails.isPending}
                    />
                </IntegrationCard>
            </Grid>
        </Grid>
    );
};
